import { AxiosError, AxiosRequestConfig } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { ApiError } from "./api";
import { useAuth } from "../hooks/auth";

interface RequestState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
}

export const useGet = <T>(
  url?: string | null,
  initialFetch = true,
  config?: AxiosRequestConfig,
  noAuth?: boolean
) => {
  const { getToken } = useAuth();
  const [state, setState] = useState<RequestState<T>>({
    data: null,
    loading: initialFetch,
    error: null,
  });

  const fetchData = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      if (url) {
        let authConfig = { ...config };
        if (!noAuth) {
          const token = await getToken();
          authConfig = {
            ...config,
            headers: {
              ...(config?.headers || {}),
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          };
        }
        const data = await api.get<T>(url, authConfig);
        setState({ data, loading: false, error: null });
        return data;
      } else {
        setState({ data: null, loading: false, error: null });
        return null;
      }
    } catch (error) {
      const apiError: ApiError = {
        message: (error as AxiosError).message,
        status: (error as AxiosError).response?.status || 500,
        data: (error as AxiosError).response?.data,
      };
      setState((prev) => ({ ...prev, loading: false, error: apiError }));
      throw apiError;
    }
  }, [url, config, getToken, noAuth]);

  useEffect(() => {
    if (initialFetch) {
      // Avoid unhandled promise rejections in React effects.
      // `fetchData` already stores the error in state for UI rendering.
      fetchData().catch(() => undefined);
    }
  }, [fetchData, initialFetch]);

  return { ...state, refetch: fetchData };
};

export const useGetLazy = <T>() => {
  const { getToken } = useAuth();
  const [state, setState] = useState<RequestState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(
    async (url: string, config?: AxiosRequestConfig, noAuth?: boolean) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        let authConfig = { ...config };
        if (!noAuth) {
          const token = await getToken();
          authConfig = {
            ...config,
            headers: {
              ...(config?.headers || {}),
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          };
        }
        const data = await api.get<T>(url, authConfig);
        setState({ data, loading: false, error: null });
        return data;
      } catch (error) {
        const apiError: ApiError = {
          message: (error as AxiosError).message,
          status: (error as AxiosError).response?.status || 500,
          data: (error as AxiosError).response?.data,
        };
        setState((prev) => ({ ...prev, loading: false, error: apiError }));
        throw apiError;
      }
    },
    [getToken]
  );

  return { ...state, execute };
};

export const usePost = <T, D = any>() => {
  const { getToken } = useAuth();
  const [state, setState] = useState<RequestState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(
    async (
      url: string,
      data?: D,
      config?: AxiosRequestConfig,
      noAuth?: boolean
    ) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        let authConfig = { ...config };
        if (!noAuth) {
          const token = await getToken();
          authConfig = {
            ...config,
            headers: {
              ...(config?.headers || {}),
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          };
        }

        const responseData = await api.post<T, D>(url, data, authConfig);
        setState({ data: responseData, loading: false, error: null });
        return responseData;
      } catch (error) {
        const apiError: ApiError = {
          message: (error as AxiosError).message,
          status: (error as AxiosError).response?.status || 500,
          data: (error as AxiosError).response?.data,
        };
        setState((prev) => ({ ...prev, loading: false, error: apiError }));
        throw apiError;
      }
    },
    [getToken]
  );

  return { ...state, execute };
};

export const usePut = <T, D = any>() => {
  const { getToken } = useAuth();
  const [state, setState] = useState<RequestState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(
    async (
      url: string,
      data?: D,
      config?: AxiosRequestConfig,
      noAuth?: boolean
    ) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        let authConfig = { ...config };
        if (!noAuth) {
          const token = await getToken();
          authConfig = {
            ...config,
            headers: {
              ...(config?.headers || {}),
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          };
        }

        const responseData = await api.put<T, D>(url, data, authConfig);
        setState({ data: responseData, loading: false, error: null });
        return responseData;
      } catch (error) {
        const apiError: ApiError = {
          message: (error as AxiosError).message,
          status: (error as AxiosError).response?.status || 500,
          data: (error as AxiosError).response?.data,
        };
        setState((prev) => ({ ...prev, loading: false, error: apiError }));
        throw apiError;
      }
    },
    [getToken]
  );

  return { ...state, execute };
};

export const useDelete = <T>() => {
  const { getToken } = useAuth();
  const [state, setState] = useState<RequestState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(
    async (url: string, config?: AxiosRequestConfig, noAuth?: boolean) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        let authConfig = { ...config };
        if (!noAuth) {
          const token = await getToken();
          authConfig = {
            ...config,
            headers: {
              ...(config?.headers || {}),
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          };
        }

        const responseData = await api.delete<T>(url, authConfig);
        setState({ data: responseData, loading: false, error: null });
        return responseData;
      } catch (error) {
        const apiError: ApiError = {
          message: (error as AxiosError).message,
          status: (error as AxiosError).response?.status || 500,
          data: (error as AxiosError).response?.data,
        };
        setState((prev) => ({ ...prev, loading: false, error: apiError }));
        throw apiError;
      }
    },
    [getToken]
  );

  return { ...state, execute };
};

export const usePatch = <T, D = any>() => {
  const { getToken } = useAuth();
  const [state, setState] = useState<RequestState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(
    async (
      url: string,
      data?: D,
      config?: AxiosRequestConfig,
      noAuth?: boolean
    ) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        let authConfig = { ...config };
        if (!noAuth) {
          const token = await getToken();
          authConfig = {
            ...config,
            headers: {
              ...(config?.headers || {}),
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          };
        }

        const responseData = await api.patch<T, D>(url, data, authConfig);
        setState({ data: responseData, loading: false, error: null });
        return responseData;
      } catch (error) {
        const apiError: ApiError = {
          message: (error as AxiosError).message,
          status: (error as AxiosError).response?.status || 500,
          data: (error as AxiosError).response?.data,
        };
        setState((prev) => ({ ...prev, loading: false, error: apiError }));
        throw apiError;
      }
    },
    [getToken]
  );

  return { ...state, execute };
};
